/**
 * Page shell for the Storefront section (/storefront): Theme, Redirects and
 * Sync status render as tabs. Theme is feature-flagged, and Theme and Sync
 * status are Trove only, so the section root falls through to the first tab
 * the user can see.
 */
import { DashboardResources } from '@src/types';
import SectionTabShell, { type SectionTab } from '@src/components/SectionTabShell';
import { isStorefrontThemeEnabled } from '@src/components/themeVersions';

export type StorefrontTab = 'theme' | 'redirects' | 'sync';

export const STOREFRONT_TABS: SectionTab<StorefrontTab>[] = [
  {
    value: 'theme',
    label: 'Theme',
    path: `/${DashboardResources.ThemeVersions}`,
    resource: DashboardResources.ThemeVersions,
    troveOnly: true,
    // Getter so the flag is read at render time, not when this module loads.
    get hidden() {
      return !isStorefrontThemeEnabled();
    },
  },
  {
    value: 'redirects',
    label: 'Redirects',
    path: `/${DashboardResources.ShopifyRedirects}`,
    resource: DashboardResources.ShopifyRedirects,
  },
  {
    value: 'sync',
    label: 'Sync status',
    path: `/${DashboardResources.ShopifySync}`,
    resource: DashboardResources.ShopifySync,
    troveOnly: true,
  },
];

const TAB_DESCRIPTIONS: Record<StorefrontTab, string> = {
  theme: 'Review storefront theme versions and deploy updates.',
  redirects: 'Product redirects configured on your Shopify storefront.',
  sync: 'Catalog, inventory, and pricing changes are queued and synced to Shopify, which rate-limits how fast updates can be sent. This page refreshes automatically so you can watch the queues drain and see when your changes will be live.',
};

interface StorefrontTabShellProps {
  tab: StorefrontTab;
  children: React.ReactNode;
}

const StorefrontTabShell = ({ tab, children }: StorefrontTabShellProps) => (
  <SectionTabShell title="Storefront" description={TAB_DESCRIPTIONS[tab]} tabs={STOREFRONT_TABS} tab={tab}>
    {children}
  </SectionTabShell>
);

export default StorefrontTabShell;
